import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Divider,
  Stack,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  useTheme,
  alpha,
  Avatar,
  IconButton,
} from "@mui/material";
import {
  Person,
  CalendarMonth,
  Groups,
  MenuBook,
  CardMembership,
  CheckCircle,
  PlayCircle,
  TrendingFlat,
  Close,
} from "@mui/icons-material";

const THEME_GOLD = "#D4AF37";
const THEME_BLUE = "#1976d2";
const THEME_DARK = "#1a1a1a";

// --- Reusable hook to load the Razorpay script ---
const useRazorpayScript = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => setIsLoaded(true);
    script.onerror = () => {
      console.error("Razorpay SDK could not be loaded.");
      setIsLoaded(false);
    };

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return isLoaded;
};

export default function Banner({
  course,
  isEnrolled,
  isInstructor,
  onEnroll,
  trailer,
  lessonCount,
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);

  const isRazorpayLoaded = useRazorpayScript();
  const token = localStorage.getItem("jwttoken");
  const url = "https://tdtlworld.com/dss-kaas-backend/";
  const isDarkMode = theme.palette.mode === "dark";

  const handlePayment = async () => {
    if (!isRazorpayLoaded || !window.Razorpay) {
      throw new Error(
        "Payment gateway is not ready. Please wait a moment and try again.",
      );
    }

    return new Promise((resolve, reject) => {
      const options = {
        key: "rzp_test_RJ2uzTo2vlQLLp",
        amount: course.price * 100,
        currency: "INR",
        name: "KaaS Course Enrollment",
        description: `Payment for ${course.title}`,
        order_id: course.order_id,
        handler: (response) => resolve(response),
        prefill: {
          name: profileData?.full_name || "KaaS User",
          email: profileData?.email,
          contact: profileData?.mobile_number,
        },
        theme: { color: THEME_GOLD },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      rzp.on("payment.failed", (response) =>
        reject(new Error(response.error.description)),
      );
    });
  };

  const handleEnrollmentProcess = async () => {
    setActionLoading(true);
    setShowConfirmModal(false);

    try {
      const isFree = !course.price || course.price === 0;
      if (isFree) {
        await onEnroll(); // Call parent enroll function
      } else {
        const paymentResponse = await handlePayment();
        await onEnroll(paymentResponse.razorpay_payment_id);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Process Failed",
        text: error.message,
        confirmButtonColor: THEME_GOLD,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const openEnrollModal = async () => {
    const isFree = !course.price || course.price === 0;
    if (!isFree) {
      setActionLoading(true);
      setShowConfirmModal(true);
      try {
        const response = await axios.get(`${url}lms/student-self-profile/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfileData(response.data);
      } catch (error) {
        console.error("Could not fetch profile for payment:", error);
        setShowConfirmModal(false);
      } finally {
        setActionLoading(false);
      }
    } else {
      setShowConfirmModal(true);
    }
  };

  if (!course) return null;

  const isPaidCourse = course.price && course.price > 0;

  return (
    <Box sx={{ position: "relative" }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          minHeight: "480px",
          display: "flex",
          alignItems: "center",
          bgcolor: isDarkMode ? "#121212" : "#FFFFFF",
          backgroundImage:
            course.cover_img && course.cover_img !== "null"
              ? isDarkMode
                ? `linear-gradient(90deg, rgba(18,18,18,1) 0%, rgba(18,18,18,0.7) 50%, rgba(18,18,18,0.2) 100%), url(${course.cover_img})`
                : `linear-gradient(90deg, rgba(245,247,250,1) 0%, rgba(245,247,250,0.8) 50%, rgba(245,247,250,0.2) 100%), url(${course.cover_img})`
              : `none`,
          backgroundSize: "cover",
          backgroundPosition: "center right",
          borderRadius: "0 0 60px 60px",
          color: isDarkMode ? "white" : "text.primary",
          zIndex: 1,
          transition: "all 0.3s ease",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Box
                  sx={{
                    display: "inline-block",
                    bgcolor: alpha(THEME_GOLD, isDarkMode ? 0.15 : 0.1),
                    px: 2,
                    py: 0.5,
                    borderRadius: "50px",
                    border: `1px solid ${alpha(THEME_GOLD, 0.3)}`,
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: THEME_GOLD,
                      fontWeight: 900,
                      letterSpacing: "2px",
                    }}
                  >
                    Expert-led Learning
                  </Typography>
                </Box>

                <Typography
                  variant="h2"
                  fontWeight={900}
                  sx={{
                    mb: 3,
                    lineHeight: 1.1,
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    textShadow: isDarkMode
                      ? "0 4px 10px rgba(0,0,0,0.3)"
                      : "none",
                    color: isDarkMode ? "white" : "#1A2027",
                  }}
                >
                  {course.title}
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={4}
                  sx={{ mb: 4 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 45,
                        height: 45,
                        border: `2px solid ${THEME_GOLD}`,
                        bgcolor: alpha(THEME_GOLD, isDarkMode ? 0.2 : 0.1),
                      }}
                    >
                      <Person sx={{ color: THEME_GOLD }} />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          opacity: isDarkMode ? 0.6 : 0.7,
                          color: isDarkMode ? "inherit" : "text.secondary",
                        }}
                      >
                        Main Instructor
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={800}>
                        {course.instructor?.name || course.instructor}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 45,
                        height: 45,
                        bgcolor: isDarkMode
                          ? "rgba(255,255,255,0.08)"
                          : alpha(theme.palette.primary.main, 0.1),
                      }}
                    >
                      <CalendarMonth
                        sx={{
                          color: isDarkMode
                            ? "rgba(255,255,255,0.8)"
                            : theme.palette.primary.main,
                        }}
                      />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          opacity: isDarkMode ? 0.6 : 0.7,
                          color: isDarkMode ? "inherit" : "text.secondary",
                        }}
                      >
                        Starts On
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={800}>
                        {new Date(course.start_date).toLocaleDateString(
                          "en-US",
                          { month: "short", day: "numeric", year: "numeric" },
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Course Info Card (Floating) */}
      <Container
        maxWidth="lg"
        sx={{ position: "relative", zIndex: 2, mt: "-220px" }}
      >
        <Grid container spacing={4} justifyContent="flex-end">
          <Grid item xs={12} md={4.5} lg={4}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <Paper
                elevation={24}
                sx={{
                  borderRadius: "32px",
                  overflow: "hidden",
                  bgcolor: isDarkMode
                    ? alpha("#1A1A1A", 0.95)
                    : "background.paper",
                  backdropFilter: "blur(10px)",
                  border: isDarkMode
                    ? `1px solid ${alpha(THEME_GOLD, 0.2)}`
                    : "none",
                  boxShadow: isDarkMode
                    ? `0 20px 60px rgba(0,0,0,0.8)`
                    : `0 20px 60px rgba(0,0,0,0.1)`,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    pt: "56.25%",
                    bgcolor: "black",
                    overflow: "hidden",
                  }}
                >
                  {trailer ? (
                    <video
                      src={
                        trailer.startsWith("http")
                          ? trailer
                          : `${url}${trailer}`
                      }
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      controls
                    />
                  ) : (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(0,0,0,0.8)",
                      }}
                    >
                      <PlayCircle
                        sx={{ fontSize: 80, color: alpha(THEME_GOLD, 0.4) }}
                      />
                    </Box>
                  )}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 20,
                      right: 20,
                      bgcolor: alpha("#000", 0.6),
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "50px",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={900}
                      sx={{ color: THEME_GOLD }}
                    >
                      PREVIEW
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 4 }}>
                  <Stack
                    direction="row"
                    alignItems="baseline"
                    spacing={1}
                    sx={{ mb: 3 }}
                  >
                    {isPaidCourse ? (
                      <>
                        <Typography
                          variant="h3"
                          fontWeight={900}
                          color={THEME_GOLD}
                        >
                          ₹{course.price}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textDecoration: "line-through" }}
                        >
                          ₹{(course.price * 1.5).toFixed(0)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="success.main"
                          fontWeight={800}
                        >
                          (33% OFF)
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        variant="h3"
                        fontWeight={900}
                        color={THEME_GOLD}
                      >
                        FREE
                      </Typography>
                    )}
                  </Stack>

                  <Typography
                    variant="subtitle2"
                    fontWeight={800}
                    sx={{ mb: 2, color: "text.primary" }}
                  >
                    Includes:
                  </Typography>
                  <Stack spacing={2} sx={{ mb: 4 }}>
                    {[
                      {
                        icon: <MenuBook fontSize="small" />,
                        text: `${lessonCount} Advanced Lessons`,
                      },
                      {
                        icon: <PlayCircle fontSize="small" />,
                        text: "Lifetime Access to Videos",
                      },
                      {
                        icon: <Groups fontSize="small" />,
                        text: "Interactive Peer Community",
                      },
                      {
                        icon: <CardMembership fontSize="small" />,
                        text: "Professional Certificate",
                      },
                    ].map((item, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          color: "text.secondary",
                        }}
                      >
                        <Box sx={{ color: THEME_GOLD, display: "flex" }}>
                          {item.icon}
                        </Box>
                        <Typography variant="body2" fontWeight={600}>
                          {item.text}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  {isInstructor ? (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() =>
                        navigate(`/db-shiksha/kaas/admin/addcourseinfo/${id}`)
                      }
                      sx={{
                        borderRadius: "16px",
                        py: 2,
                        fontWeight: 900,
                        bgcolor: THEME_GOLD,
                        fontSize: "1rem",
                      }}
                    >
                      EDIT COURSE DETAILS
                    </Button>
                  ) : isEnrolled ? (
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<CheckCircle />}
                      onClick={() =>
                        navigate(`/db-shiksha/kaas/student/course/${id}`)
                      }
                      sx={{
                        borderRadius: "16px",
                        py: 2,
                        fontWeight: 900,
                        bgcolor: THEME_GOLD,
                        fontSize: "1rem",
                        boxShadow: `0 10px 25px ${alpha(THEME_GOLD, 0.4)}`,
                      }}
                    >
                      RESUME LEARNING
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={openEnrollModal}
                      disabled={isPaidCourse && !isRazorpayLoaded}
                      sx={{
                        borderRadius: "16px",
                        py: 2,
                        fontWeight: 900,
                        bgcolor: THEME_GOLD,
                        fontSize: "1rem",
                        boxShadow: `0 10px 25px ${alpha(THEME_GOLD, 0.4)}`,
                        "&:hover": {
                          transform: "scale(1.02)",
                          bgcolor: alpha(THEME_GOLD, 0.9),
                        },
                        transition: "0.2s",
                      }}
                    >
                      {actionLoading
                        ? "Processing..."
                        : isPaidCourse
                          ? "PAY & ENROLL NOW"
                          : "ENROLL FOR FREE"}
                    </Button>
                  )}
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Enrollment Dialog */}
      <Dialog
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        PaperProps={{ sx: { borderRadius: "24px", p: 1, maxWidth: "450px" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h5" fontWeight={900}>
            Ready to Start?
          </Typography>
          <IconButton onClick={() => setShowConfirmModal(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            You're joining <strong>{course.title}</strong>. This enrollment
            gives you full access to all lectures and resources.
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: "20px",
              bgcolor: alpha(THEME_GOLD, 0.05),
              borderColor: THEME_GOLD,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography fontWeight={700}>Total Amount:</Typography>
              <Typography variant="h5" fontWeight={900} color={THEME_GOLD}>
                {isPaidCourse ? `₹${course.price}` : "FREE"}
              </Typography>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            fullWidth
            onClick={() => setShowConfirmModal(false)}
            sx={{ borderRadius: "50px", fontWeight: 700 }}
          >
            Later
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleEnrollmentProcess}
            sx={{
              borderRadius: "50px",
              py: 1.5,
              bgcolor: THEME_GOLD,
              fontWeight: 900,
            }}
          >
            {isPaidCourse ? "PROCEED TO PAY" : "ENROLL NOW"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
